// @flow

import React, { Fragment } from 'react';
import moment from 'moment';

import { SPACING, Row, Col, Flex, BreadcrumbsHeader } from 'clark-styles';

import type { EngagementsType } from 'types/engagements';
import type { ClientsType } from 'types/clients';
import type { SessionType, SessionsType } from 'types/sessions';
import type { SubjectsType } from 'types/subjects';

import { getEngagementClientDetails, getEngagementSubjectDetails } from 'utils/helpers/engagements';
import {
  editRecurringEnabled,
  getRemainingRecurringSessions,
  getSessionDuration,
  getSessionDurationInHours,
  getSessionWageInDollars,
} from 'utils/helpers/sessions';

import SessionsDeleteModal from 'containers/sessions/delete-modal';
import SessionsCanceledModal from 'containers/sessions/canceled-modal';
import EditSession from 'containers/sessions/edit-form/modal';

import CalculatedDataHighlight, { DataHighlight } from 'components/data-highlight';

import SESSION_STATUSES from 'constants/session-statuses';

import { formatStatus, formatTime, isDeleteDisabled } from './helpers';

import {
  GridWrapper,
  ActionsCol,
  Header,
  DataWrapper,
  InfoItem,
  SubjectIcon,
  LocationIcon,
  InfoCopy,
} from './styles';

const breadcrumbs = [{ path: '/sessions', displayName: 'Back' }];

type PropsType = {
  session: SessionType,
  sessions: SessionsType,
  clients: ClientsType,
  engagements: EngagementsType,
  subjects: SubjectsType,
};

const SessionsViewHeader = ({ clients, engagements, subjects, session, sessions }: PropsType) => {
  const {
    attributes: {
      createdAt,
      billCreatedAt,
      startTime,
      endTime,
      status: sessionStatus,
      tutoringEngagementId,
      locationRaw,
      serviceSessionRecurrenceGroupingId,
    },
  } = session;

  const formattedStartDate = moment(startTime).format('MMM Do');
  const engagement = engagements[tutoringEngagementId];
  const { clientName } = getEngagementClientDetails(engagement, clients);
  const { subjectName } = getEngagementSubjectDetails(engagement, subjects);

  const isCancelButtonDisabled = sessionStatus === SESSION_STATUSES.canceled;
  const isDeleteButtonDisabled = isDeleteDisabled(billCreatedAt, createdAt);

  const sessionWageInDollars = getSessionWageInDollars(session);

  const isEditRecurringEnabled = editRecurringEnabled(
    session,
    sessions,
    serviceSessionRecurrenceGroupingId,
    startTime,
  );

  const remainingRecurringSessions = getRemainingRecurringSessions(session, sessions);

  return (
    <Fragment>
      <BreadcrumbsHeader breadcrumbs={breadcrumbs} title="Session Details" />
      <GridWrapper>
        <Row>
          <Col sm={12} mdOffset={3} md={6} lgOffset={3} lg={6}>
            <Header>{`${clientName}'s Session on ${formattedStartDate}`}</Header>
            {subjectName && (
              <InfoItem>
                <SubjectIcon />
                <InfoCopy>{subjectName}</InfoCopy>
              </InfoItem>
            )}
            {locationRaw && (
              <InfoItem>
                <LocationIcon />
                <InfoCopy>Location:</InfoCopy>
                <span>{locationRaw}</span>
              </InfoItem>
            )}
            <Flex margin={`${SPACING.S_2} 0`}>
              <DataWrapper>
                <DataHighlight description="Status" data={formatStatus(sessionStatus)} />
              </DataWrapper>
              <DataWrapper>
                <CalculatedDataHighlight
                  description="Tutor earnings"
                  type="currency"
                  data={sessionWageInDollars}
                />
              </DataWrapper>
            </Flex>
            <Flex>
              <DataWrapper>
                <DataHighlight description="Time" data={formatTime(startTime, endTime)} />
              </DataWrapper>
              <DataWrapper>
                <CalculatedDataHighlight
                  description="Session duration"
                  type="time"
                  data={getSessionDurationInHours(getSessionDuration(session))}
                />
              </DataWrapper>
            </Flex>
          </Col>
          <ActionsCol sm={12} md={3} lg={3}>
            <EditSession
              isEditRecurringEnabled={isEditRecurringEnabled}
              remainingRecurringSessions={remainingRecurringSessions}
              session={session}
              sessions={sessions}
            />
            <SessionsCanceledModal
              disabled={isCancelButtonDisabled}
              session={session}
              clientName={clientName}
            />
            <SessionsDeleteModal
              isButton
              disabled={isDeleteButtonDisabled}
              session={session}
              deleteRecurringEnabled={isEditRecurringEnabled}
              remainingRecurringSessions={remainingRecurringSessions}
            />
          </ActionsCol>
        </Row>
      </GridWrapper>
    </Fragment>
  );
};

export default SessionsViewHeader;



// WEBPACK FOOTER //
// ./src/components/sessions/view/header/index.js